import { titleCaseKebabSlug } from '@/lib/title-case-kebab-slug';

/**
 * Hardcoded CLT neighborhood slugs + display names for the placeholder
 * routes scaffolded in WEB-86 / static fallback paths. Order matches the
 * PIP-176 operator list / PIP-178 seed. Live ordinal copy ("NN of MM")
 * reads `position` + `totalCount` from the neighborhoods API — do not
 * hardcode "of 07" in UI from this list length.
 */
export const CLT_NEIGHBORHOOD_SLUGS = [
  'uptown',
  'south-end',
  'plaza-midwood',
  'noda',
  'elizabeth',
  'dilworth',
  'south-park',
  'loso',
  'freemorewest',
  'mora',
  'camp-north-end',
  'myers-park',
  'ballantyne',
  'university-city',
  'historic-west-end',
  'optimist-park',
  'cotswold',
  'wesley-heights',
  'wilmore',
  'cherry',
  'sedgefield',
  'belmont',
  'steele-creek',
  'montford',
  'villa-heights',
] as const;

export type CltNeighborhoodSlug = (typeof CLT_NEIGHBORHOOD_SLUGS)[number];

const NAME_BY_SLUG: Readonly<Record<CltNeighborhoodSlug, string>> = {
  uptown: 'Uptown',
  'south-end': 'South End',
  'plaza-midwood': 'Plaza Midwood',
  noda: 'NoDa',
  elizabeth: 'Elizabeth',
  dilworth: 'Dilworth',
  'south-park': 'SouthPark',
  loso: 'LoSo',
  freemorewest: 'FreeMoreWest',
  mora: 'MoRA',
  'camp-north-end': 'Camp North End',
  'myers-park': 'Myers Park',
  ballantyne: 'Ballantyne',
  'university-city': 'University City',
  'historic-west-end': 'Historic West End',
  'optimist-park': 'Optimist Park',
  cotswold: 'Cotswold',
  'wesley-heights': 'Wesley Heights',
  wilmore: 'Wilmore',
  cherry: 'Cherry',
  sedgefield: 'Sedgefield',
  belmont: 'Belmont',
  'steele-creek': 'Steele Creek',
  montford: 'Montford',
  'villa-heights': 'Villa Heights',
};

export function isCltNeighborhoodSlug(value: string): value is CltNeighborhoodSlug {
  return (CLT_NEIGHBORHOOD_SLUGS as readonly string[]).includes(value);
}

export function getNeighborhoodNameFromSlug(slug: string): string | null {
  return isCltNeighborhoodSlug(slug) ? NAME_BY_SLUG[slug] : null;
}

function normalizeNeighborhoodName(value: string): string {
  return value.toLowerCase().replace(/\s+/g, '');
}

/**
 * Case- and spacing-insensitive index of display name → slug, derived from
 * {@link NAME_BY_SLUG} so the two can never drift.
 *
 * The key is the display name lowercased with all whitespace stripped, which
 * is what makes `SouthPark`, `South Park` and `south park` all resolve to
 * `south-park`. The seed's canonical spelling is one word, but the discover
 * wire carries whatever the venue's `Location.neighborhood` column holds, and
 * that column is operator-entered.
 */
const SLUG_BY_NORMALIZED_NAME: ReadonlyMap<string, CltNeighborhoodSlug> = new Map(
  (Object.entries(NAME_BY_SLUG) as [CltNeighborhoodSlug, string][]).map(([slug, name]) => [
    normalizeNeighborhoodName(name),
    slug,
  ]),
);

/**
 * Resolve a neighborhood **display name** (as carried by
 * `DiscoverEvent.neighborhood`) back to its canonical slug.
 *
 * WEB-670 needs this because the discover wire projects only the human label
 * (`"Plaza Midwood"`), while `useNeighborhoodFootprints` keys its path lookup
 * on the slug (`"plaza-midwood"`). Inverting the same table that produces the
 * forward lookup is deliberate: slugifying at the call site silently
 * mis-derives every non-uniform name in the list (`SouthPark` → `southpark`,
 * not `south-park`; `NoDa` → `noda` only by luck; `FreeMoreWest`,
 * `MoRA`, `LoSo` likewise).
 *
 * Returns `null` for an unknown name so the row renders honest absence — the
 * hood label without a glyph — instead of a fabricated or fallback shape.
 */
export function getNeighborhoodSlugFromName(name: string): CltNeighborhoodSlug | null {
  return SLUG_BY_NORMALIZED_NAME.get(normalizeNeighborhoodName(name)) ?? null;
}

/**
 * Resolve a discover-wire neighborhood value that may be either a display
 * name (`"Plaza Midwood"`) or a stored slug (`"plaza-midwood"`).
 *
 * WEB-787: the discover projector fails open to the stored slug when the
 * label cache is cold, so rows must accept both forms. `getNeighborhoodSlugFromName`
 * stays name-only so a naive slugify at other call sites cannot silently
 * succeed.
 */
export function resolveNeighborhoodSlug(value: string): CltNeighborhoodSlug | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const asSlug = trimmed.toLowerCase();
  if (isCltNeighborhoodSlug(asSlug)) return asSlug;
  return getNeighborhoodSlugFromName(trimmed);
}

/**
 * What a backend `type: 'location'` suggestion (autocomplete row) actually
 * represents — mirrors the backend's `locationKind` field
 * (`services/backend-api/src/services/eventSuggestions.ts`).
 */
export type LocationSuggestionKind = 'city' | 'neighborhood';

/**
 * Classify a `type: 'location'` autocomplete suggestion as a city or a
 * neighborhood (WEB-965). Shared by every surface that consumes the
 * `/events/search/suggestions` endpoint's `location` rows — today that's
 * the Discover autocomplete (`useDiscoverSearch`) and the global header
 * search (`SearchSheet`) — so the two never drift on the decision.
 *
 * Precedence, in order:
 *  1. Match `value` (and `label`, when given — the two are identical for
 *     every `location` row the backend emits today, but a caller may only
 *     have one of them) against the known CLT neighborhood list. This is
 *     the defensive check: it holds even when the backend's `locationKind`
 *     field is absent (an older backend deploy) or disagrees.
 *  2. Fall back to the backend's `locationKind` field when present.
 *  3. Default to `'city'` — the safe, no-op-filter interpretation — when
 *     neither signal says `'neighborhood'`.
 */
export function classifyLocationSuggestionKind(
  value: string,
  locationKind?: LocationSuggestionKind,
  label?: string,
): LocationSuggestionKind {
  if (resolveNeighborhoodSlug(value) !== null) return 'neighborhood';
  if (label !== undefined && resolveNeighborhoodSlug(label) !== null) return 'neighborhood';
  if (locationKind === 'neighborhood') return 'neighborhood';
  return 'city';
}

/**
 * Canonical display name + slug for a row neighborhood value.
 *
 * Never returns a hyphenated lowercase slug as `displayName`. Known CLT
 * slugs and names resolve to {@link NAME_BY_SLUG}; anything else is
 * title-cased the same way the neighborhood filter rail does
 * (`university-city` → `University City`).
 */
export interface NeighborhoodIdentity {
  readonly slug: CltNeighborhoodSlug | null;
  readonly displayName: string;
}

export function resolveNeighborhoodIdentity(value: string): NeighborhoodIdentity {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { slug: null, displayName: '' };
  }
  const slug = resolveNeighborhoodSlug(trimmed);
  if (slug) {
    return { slug, displayName: NAME_BY_SLUG[slug] };
  }
  return { slug: null, displayName: titleCaseKebabSlug(trimmed) };
}

/**
 * Canonical name when the value is a known CLT neighborhood.
 * An unresolved value is omitted so a raw slug never prints.
 */
export function knownNeighborhoodDisplayName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const identity = resolveNeighborhoodIdentity(trimmed);
  if (identity.slug == null) return null;
  return identity.displayName;
}
